import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaUsers, FaUserCheck, FaChartLine, FaMapMarkerAlt, FaCalendarCheck, FaSearch, FaCalendarAlt, FaUserTimes, FaUserSlash, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { MdPendingActions, MdOutlinePersonPin } from "react-icons/md";
import { BsFillPersonCheckFill, BsClockHistory } from "react-icons/bs";
import { getAgents, getRoles, getAllLeads, getAllPotentialLeads, getLeadStatistics, getSourcingStatistics, getStateTransitions } from "../Api/api";
import { useRegions } from '../../context/RegionContext';
import { MetricShimmer, ChartShimmer, TableRowShimmer, ActivityShimmer } from "./Shimmer";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  LabelList
} from "recharts";
import ReactFlow, { 
  Handle, 
  Position, 
  MarkerType,
  useNodesState,
  useEdgesState,
  ConnectionMode
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useAuth } from "../Auth/AuthContext";
import moment from 'moment';
import CustomDatePicker from '../Common/DatePicker';
import { erSourcesListConstant } from "../../utils/constants";

const Dashboard = () => {
  const { selectedRegion, refreshTrigger, updateSelectedRegion } = useAuth();
  const { regions: contextRegions, getRegionName } = useRegions();
  // Separate loading states for different data fetching operations
  const [isMetricsLoading, setIsMetricsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  });
  const [toDate, setToDate] = useState(() => new Date());
  const [source, setSource] = useState(null);  // Add source state
  const [selectedRole, setSelectedRole] = useState(null); // Add role state
  const [agentStats, setAgentStats] = useState({ items: [], total: 0, allItems: [] });
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    total_leads: 0,
    processed_leads: 0,
    qualified_leads: 0,
    rejected_leads: 0,
    total_er: 0,
    passed: 0,
    pending_registration: 0,
    pending_training: 0,
    in_training: 0,
    active: 0,
    failed: 0,
    suspended: 0,
    regions: [],
    recentActivities: [],
    sourceData: []
  });
  const [errors, setErrors] = useState({});  // New state for individual errors
  const [showFlowchart, setShowFlowchart] = useState(false);
  const [showLeadFlow, setShowLeadFlow] = useState(false);
  const [showERFlow, setShowERFlow] = useState(false);
  const [stateTransitions, setStateTransitions] = useState([]);
  const [isFlowLoading, setIsFlowLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('er'); // Add new state for tab management
  const [localSelectedRegion, setLocalSelectedRegion] = useState(""); // Add local state for cluster filter

  const COLORS = {
    leads: {
      total: { bg: 'bg-blue-100', text: 'text-blue-600', fill: '#60A5FA' },
      processed: { bg: 'bg-emerald-100', text: 'text-emerald-600', fill: '#34D399' },
      qualified: { bg: 'bg-violet-100', text: 'text-violet-600', fill: '#A78BFA' },
      rejected: { bg: 'bg-rose-100', text: 'text-rose-600', fill: '#FB7185' }
    },
    er: {
      total: { bg: 'bg-purple-100', text: 'text-purple-600', fill: '#C084FC' },
      pending: { bg: 'bg-amber-100', text: 'text-amber-600', fill: '#FCD34D' },
      training: { bg: 'bg-cyan-100', text: 'text-cyan-600', fill: '#22D3EE' },
      active: { bg: 'bg-teal-100', text: 'text-teal-600', fill: '#2DD4BF' },
      passed: { bg: 'bg-indigo-100', text: 'text-indigo-600', fill: '#818CF8' },
      failed: { bg: 'bg-slate-100', text: 'text-slate-600', fill: '#94A3B8' },
      suspended: { bg: 'bg-orange-100', text: 'text-orange-600', fill: '#FB923C' }
    },
    sourceChart: [
      '#60A5FA', // blue
      '#34D399', // emerald
      '#A78BFA', // violet
      '#FB7185', // rose
      '#FCD34D', // amber
      '#22D3EE', // cyan
      '#2DD4BF', // teal
      '#818CF8'  // indigo
    ],
    regionalChart: [  
      '#059669', // emerald-600
      '#10B981', // emerald-500
      '#34D399', // em  erald-400
      '#6EE7B7', // emerald-300
      '#A7F3D0', // emerald-200
      '#047857', // emerald-700
      '#065F46', // emerald-800
      '#064E3B'  // emerald-900
    ]
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    return moment(date).format('YYYY-MM-DD');
  };

  const formatDateForAPI = (date, type = 'from') => {
    if (!date) return null;
    
    const momentDate = moment(date);
    
    if (type === 'to') {
      momentDate.endOf('day');
    } else {
      momentDate.startOf('day');
    }
    
    return momentDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
  };

  // Remove the regions state since we're using context
  const [filteredRegions, setFilteredRegions] = useState([]);
  const [roles, setRoles] = useState([]);

  // Remove the fetchFilters useEffect since we're using context
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesData = await getRoles();
        setRoles(rolesData || []);
      } catch (err) {
        console.error("Error fetching roles:", err);
        setErrors(prev => ({
          ...prev,
          filters: "Failed to fetch filters"
        }));
      }
    };
    fetchRoles();
  }, []);

  // Update the region filtering logic
  useEffect(() => {
    const selectedRegionName = getRegionName(selectedRegion);
    
    const filtered = contextRegions.filter(region => {
      const regionName = region.name.toLowerCase();
      return regionName.includes(selectedRegionName);
    });
    
    setFilteredRegions(filtered);
  }, [contextRegions, selectedRegion, getRegionName]);

  // Add sources list from lead generation form for ER tab
  const erSourcesList = erSourcesListConstant;

  // Update DateFilter component to include role and region filters
  const DateFilter = ({ onDateChange, onSourceChange, onRoleChange, onRegionChange, showSourceFilter }) => {
    const { updateSelectedRegion } = useAuth();
    const validateDateRange = (fromDate, toDate) => {
      if (!fromDate || !toDate) return true;
      
      const from = new Date(fromDate);
      const to = new Date(toDate);
      const diffTime = Math.abs(to - from);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays <= 30;
    };

    const handleDateChange = (type, value) => {
      let newFromDate = type === 'from' ? value : fromDate;
      let newToDate = type === 'to' ? value : toDate;

      if (type === 'clear') {
        onDateChange('clear');
        return;
      }

      // Convert to Date objects for validation
      const fromDateObj = newFromDate ? new Date(newFromDate) : null;
      const toDateObj = newToDate ? new Date(newToDate) : null;

      if (!validateDateRange(fromDateObj, toDateObj)) {
        alert('Date range cannot exceed 30 days');
        return;
      }

      onDateChange(type, value);
    };

    const handleSourceChange = (e) => {
      const value = e.target.value || null;
      onSourceChange(value);
    };

    const handleRoleChange = (e) => {
      const value = e.target.value || null;
      onRoleChange(value);
    };

    const handleRegionChange = (e) => {
      const value = e.target.value || null;
      onRegionChange(value);
    };

    return (
      <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200 mb-6 relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FaCalendarAlt className="text-gray-500 mr-2" />
            <span className="text-gray-700 font-medium">Filters</span>
          </div>
          <button
            onClick={() => handleDateChange('clear')}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear Filters
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          {/* From Date */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">From Date</label>
            <CustomDatePicker
              selected={fromDate}
              onChange={(date) => handleDateChange('from', date)}
              placeholderText="Select from date"
              maxDate={toDate || new Date()}
              dateFormat="dd/MM/yyyy"
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">To Date</label>
            <CustomDatePicker
              selected={toDate}
              onChange={(date) => handleDateChange('to', date)}
              placeholderText="Select to date"
              minDate={fromDate}
              maxDate={new Date()}
              dateFormat="dd/MM/yyyy"
            />
          </div>

          {/* Source - Only show if showSourceFilter is true */}
          {showSourceFilter && (
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Source</label>
              <div className="relative">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer text-sm appearance-none bg-white"
                  onChange={handleSourceChange}
                  value={source || ""}
                >
                  <option value="">All Sources</option>
                  {activeTab === 'er' ? (
                    erSourcesList.map((source, index) => (
                      <option key={index} value={source}>
                        {source}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="JOB_HAI">JOB HAI</option>
                      <option value="PHONEBOOK">PHONEBOOK</option>
                      <option value="REFERRAL">REFERRAL</option>
                      <option value="FACEBOOK">FACEBOOK</option>
                    </>
                  )}
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                    <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          )}

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Role</label>
            <div className="relative">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer text-sm appearance-none bg-white"
                onChange={handleRoleChange}
                value={selectedRole || ""}
              >
                <option value="">All Roles</option>
                {roles.filter(role => role.id !== 18).map(role => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Region */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Cluster</label>
            <div className="relative">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer text-sm appearance-none bg-white"
                onChange={handleRegionChange}
                value={localSelectedRegion || ""}
              >
                <option value="">All Clusters</option>
                {filteredRegions.map(region => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleDateChange = (type, date) => {
    // Set both loading states to true when date changes
    setIsMetricsLoading(true);
    setIsStatsLoading(true);
    
    if (type === 'clear') {
      setFromDate(null);
      setToDate(null);
      setSource(null);
      setSelectedRole(null);
      setSelectedRegion(null);
    } else if (type === 'from') {
      setFromDate(date ? new Date(date) : null);
    } else {
      setToDate(date ? new Date(date) : null);
    }
    
    setCurrentPage(1);
  };

  const handleSourceChange = (newSource) => {
    setIsMetricsLoading(true);
    setIsStatsLoading(true);
    setSource(newSource);
    setCurrentPage(1);
  };

  const handleRoleChange = (newRole) => {
    setIsMetricsLoading(true);
    setIsStatsLoading(true);
    setSelectedRole(newRole);
    setCurrentPage(1);
  };

  const handleRegionChange = (newRegion) => {
    setIsMetricsLoading(true);
    setIsStatsLoading(true);
    setLocalSelectedRegion(newRegion);
    setCurrentPage(1);
  };

  const fetchDashboardData = async () => {
    setIsMetricsLoading(true);
    const newErrors = {};
    let sourcingLeads = [];
    let potentialLeads = [];

    try {
      // Get metrics from the new endpoint
      const formattedFromDate = formatDateForAPI(fromDate, 'from');
      const formattedToDate = formatDateForAPI(toDate, 'to');
      
      // Format source based on active tab - send as string for ER tab
      const formattedSource = source ? (activeTab === 'er' ? source : source.replace(/\s+/g, '_').toUpperCase()) : null;
      
      const metricsResponse = await getSourcingStatistics(
        formattedFromDate, 
        formattedToDate, 
        formattedSource,
        selectedRole,
        localSelectedRegion // Use localSelectedRegion instead of selectedRegion
      );
      
      // Get potential leads for source distribution and trend data (single call)
      try {
        const potentialLeadsResponse = await getAllPotentialLeads(20, 0);
        potentialLeads = Array.isArray(potentialLeadsResponse.items) ? potentialLeadsResponse.items : [];
      } catch (err) {
        console.error("Error fetching potential leads:", err);
        newErrors.potentialLeads = "Failed to fetch potential leads data";
      }

      // Get last 10 days trend data (reuse potentialLeads from above)
      const trendData = [];
      const today = new Date();
      const tenDaysAgo = new Date();
      tenDaysAgo.setDate(today.getDate() - 10);

      try {
        const trendLeads = potentialLeads;
        
        // Create a map for the last 10 days
        for (let i = 0; i < 10; i++) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().split('T')[0];
          trendData[dateStr] = 0;
        }

        // Fill in the actual data
        // trendLeads.forEach(lead => {
        //   const date = new Date(lead.created_at).toISOString().split('T')[0];
        //   if (trendData.hasOwnProperty(date)) {
        //     trendData[date]++;
        //   }
        // });
        
        trendLeads.forEach((lead) => {
          if (lead.created_at) {
            const leadDate = new Date(lead.created_at);

            // Check if the created_at value is valid
            if (!isNaN(leadDate.getTime())) {
              const dateStr = leadDate.toISOString().split("T")[0];

              if (trendData.hasOwnProperty(dateStr)) {
                trendData[dateStr]++;
              }
            } else {
              console.warn(
                `Invalid date value for lead created_at: ${lead.created_at}`
              );
            }
          } else {
            console.warn("Missing created_at for lead");
          }
        });
      } catch (err) {
        console.error("Error fetching trend data:", err);
        newErrors.trendData = "Failed to fetch trend data";
      }

      try {
        const sourcingLeadsResponse = await getAllLeads(20, 0, formattedFromDate, formattedToDate);
        sourcingLeads = Array.isArray(sourcingLeadsResponse.items) ? sourcingLeadsResponse.items : [];
      } catch (err) {
        console.error("Error fetching sourcing leads:", err);
        newErrors.sourcingLeads = "Failed to fetch sourcing leads data";
      }

      // Process regions data if sourcing leads are available
      const regionsData = {};
      if (sourcingLeads.length > 0) {
        sourcingLeads
          .filter(lead => lead.status !== 'REJECTED')
          .forEach(lead => {
            const region = lead.details_dict?.CLUSTER?.split(':')[1]?.trim() || 'Unknown';
            regionsData[region] = (regionsData[region] || 0) + 1;
          });
      }

      // Process source data from potential leads
      const sourceData = {};
      if (potentialLeads.length > 0) {
        potentialLeads.forEach(lead => {
          const source = lead.source || 'Unknown';
          sourceData[source] = (sourceData[source] || 0) + 1;
        });
      }

      setDashboardData({
        ...metricsResponse,
        recentActivities: processRecentActivities(sourcingLeads),
        regions: Object.entries(regionsData).map(([name, earlyRegistrations]) => ({
          name,
          earlyRegistrations
        })),
        sourceData: Object.entries(sourceData).map(([source, count]) => ({
          source,
          count
        })),
        leadsByDay: Object.entries(trendData)
          .sort((a, b) => new Date(a[0]) - new Date(b[0]))
          .map(([date, count]) => ({
            date: new Date(date).toLocaleDateString(),
            count
          }))
      });

    } catch (err) {
      console.error("Error fetching metrics:", err);
      newErrors.metrics = "Failed to fetch metrics data";
    }

    setErrors(newErrors);
    setIsMetricsLoading(false);
  };

  const processRecentActivities = (leads) => {
    return leads
      .map(lead => ({
        id: `lead-${lead.id}`,
        type: "Registration",
        message: `${lead.name} registered from ${lead.details_dict?.CLUSTER?.split(':')?.[1]?.trim() || 'Unknown'} cluster`,
        time: new Date(lead.created_at).toLocaleString(),
        timestamp: new Date(lead.created_at)
      }))
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 10);
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fromDate, toDate, source, selectedRole, localSelectedRegion, refreshTrigger]); // Add refreshTrigger to dependency array

  useEffect(() => {
    const fetchAgentStats = async () => {
      try {
        setIsStatsLoading(true);
        const formattedFromDate = formatDateForAPI(fromDate, 'from');
        const formattedToDate = formatDateForAPI(toDate, 'to');
        
        const formattedSource = source ? (activeTab === 'er' ? source : source.replace(/\s+/g, '_').toUpperCase()) : null;
        
        const response = await getLeadStatistics(
          20,
          0,
          formattedFromDate,
          formattedToDate,
          formattedSource,
          selectedRole,
          localSelectedRegion
        );

        setAgentStats(prev => ({
          ...response,
          allItems: response.items,
          items: response.items
        }));
        setHasMore(response.items.length === 20);
      } catch (err) {
        console.error("Error fetching agent statistics:", err);
      } finally {
        setIsStatsLoading(false);
      }
    };

    fetchAgentStats();
  }, [fromDate, toDate, source, selectedRole, localSelectedRegion, activeTab, refreshTrigger]);

  const loadMoreData = async () => {
    if (isLoadingMore || !hasMore) return;

    try {
      setIsLoadingMore(true);
      const formattedFromDate = formatDateForAPI(fromDate, 'from');
      const formattedToDate = formatDateForAPI(toDate, 'to');
      
      const response = await getLeadStatistics(
        20,
        agentStats.allItems.length,
        formattedFromDate,
        formattedToDate,
        source,
        selectedRole,
        localSelectedRegion
      );

      setAgentStats(prev => ({
        ...prev,
        total: response.total,
        allItems: [...prev.allItems, ...response.items],
        items: [...prev.allItems, ...response.items]
      }));
      setHasMore(response.items.length === 20);
    } catch (err) {
      console.error("Error loading more agent statistics:", err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1);

    // Filter from all loaded items
    const filtered = agentStats.allItems.filter(agent =>
      agent.created_by.toLowerCase().includes(query)
    );

    setAgentStats(prev => ({
      ...prev,
      items: filtered
    }));

    // If we're searching and have few results, try loading more data
    if (query && filtered.length < 20 && hasMore) {
      loadMoreData();
    }
  };

  // Modify the pagination calculation to work with filtered items
  const totalPages = Math.ceil(agentStats.items.length / itemsPerPage);

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return agentStats.items.slice(startIndex, endIndex);
  };

  // Add handlePageChange function
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Update pagination buttons section
  const renderPaginationButtons = () => {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisibleButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

    // Adjust start if we're near the end
    if (endPage - startPage + 1 < maxVisibleButtons) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center space-x-1">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium ${
            currentPage === 1
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          First
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium ${
            currentPage === 1
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          Previous
        </button>
        {startPage > 1 && (
          <>
            <span className="px-2 py-2">...</span>
          </>
        )}
        {pages.map(page => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
              currentPage === page
                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
            }`}
          >
            {page}
          </button>
        ))}
        {endPage < totalPages && (
          <>
            <span className="px-2 py-2">...</span>
          </>
        )}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium ${
            currentPage === totalPages
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          Next
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          className={`relative inline-flex items-center px-2 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium ${
            currentPage === totalPages
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:bg-gray-50'
          }`}
        >
          Last
        </button>
      </div>
    );
  };

  // Custom node component for the flowchart
  const CustomNode = ({ data }) => {
    return (
      <div className={`px-4 py-2 shadow-lg rounded-lg border ${data.className}`}>
        <Handle type="target" position={Position.Top} className="w-2 h-2" />
        <div className="flex items-center">
          {data.icon && <data.icon className="mr-2" />}
          <div>
            <div className="font-semibold">{data.label}</div>
            <div className="text-sm">{data.value}</div>
          </div>
        </div>
        <Handle type="source" position={Position.Bottom} className="w-2 h-2" />
      </div>
    );
  };

  const nodeTypes = {
    custom: CustomNode,
  };

  // Generate flowchart nodes and edges based on dashboard data
  const generateFlowchartElements = () => {
    const nodes = [
      // Lead Flow Nodes
      {
        id: '1',
        type: 'custom',
        position: { x: 400, y: 0 },
        data: {
          label: 'Total Leads',
          value: dashboardData.total_leads || 0,
          className: 'bg-blue-50 border-blue-200',
          icon: FaUsers
        }
      },
      {
        id: '2',
        type: 'custom',
        position: { x: 400, y: 100 },
        data: {
          label: 'Processed Leads',
          value: dashboardData.processed_leads || 0,
          className: 'bg-emerald-50 border-emerald-200',
          icon: FaUserCheck
        }
      },
      {
        id: '3',
        type: 'custom',
        position: { x: 200, y: 200 },
        data: {
          label: 'Qualified Leads',
          value: dashboardData.qualified_leads || 0,
          className: 'bg-violet-50 border-violet-200',
          icon: BsFillPersonCheckFill
        }
      },
      {
        id: '4',
        type: 'custom',
        position: { x: 600, y: 200 },
        data: {
          label: 'Rejected Leads',
          value: dashboardData.rejected_leads || 0,
          className: 'bg-rose-50 border-rose-200',
          icon: FaUserTimes
        }
      },
      // ER Flow Nodes
      {
        id: '5',
        type: 'custom',
        position: { x: 200, y: 300 },
        data: {
          label: 'Total Passed',
          value: dashboardData.passed || 0,
          className: 'bg-indigo-50 border-indigo-200',
          icon: FaCalendarCheck
        }
      },
      {
        id: '6',
        type: 'custom',
        position: { x: 0, y: 400 },
        data: {
          label: 'Pending Registration',
          value: dashboardData.pending_registration || 0,
          className: 'bg-amber-50 border-amber-200',
          icon: MdPendingActions
        }
      },
      {
        id: '7',
        type: 'custom',
        position: { x: 200, y: 400 },
        data: {
          label: 'Pending Training',
          value: dashboardData.pending_training || 0,
          className: 'bg-cyan-50 border-cyan-200',
          icon: BsClockHistory
        }
      },
      {
        id: '8',
        type: 'custom',
        position: { x: 400, y: 400 },
        data: {
          label: 'In Training',
          value: dashboardData.in_training || 0,
          className: 'bg-teal-50 border-teal-200',
          icon: FaUserCheck
        }
      },
      {
        id: '9',
        type: 'custom',
        position: { x: 200, y: 500 },
        data: {
          label: 'Active',
          value: dashboardData.active || 0,
          className: 'bg-emerald-50 border-emerald-200',
          icon: FaUsers
        }
      },
      {
        id: '10',
        type: 'custom',
        position: { x: 0, y: 500 },
        data: {
          label: 'Failed',
          value: dashboardData.failed || 0,
          className: 'bg-slate-50 border-slate-200',
          icon: FaUserTimes
        }
      },
      {
        id: '11',
        type: 'custom',
        position: { x: 400, y: 500 },
        data: {
          label: 'Suspended',
          value: dashboardData.suspended || 0,
          className: 'bg-orange-50 border-orange-200',
          icon: FaUserSlash
        }
      }
    ];

    const edges = [
      // Lead Flow Edges
      {
        id: 'e1-2',
        source: '1',
        target: '2',
        animated: true,
        style: { stroke: '#60A5FA' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      {
        id: 'e2-3',
        source: '2',
        target: '3',
        animated: true,
        style: { stroke: '#34D399' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      {
        id: 'e2-4',
        source: '2',
        target: '4',
        animated: true,
        style: { stroke: '#FB7185' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      // ER Flow Edges
      {
        id: 'e3-5',
        source: '3',
        target: '5',
        animated: true,
        style: { stroke: '#818CF8' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      {
        id: 'e5-6',
        source: '5',
        target: '6',
        animated: true,
        style: { stroke: '#FCD34D' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      {
        id: 'e5-7',
        source: '5',
        target: '7',
        animated: true,
        style: { stroke: '#22D3EE' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      {
        id: 'e5-8',
        source: '5',
        target: '8',
        animated: true,
        style: { stroke: '#2DD4BF' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      {
        id: 'e8-9',
        source: '8',
        target: '9',
        animated: true,
        style: { stroke: '#34D399' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      {
        id: 'e8-10',
        source: '8',
        target: '10',
        animated: true,
        style: { stroke: '#94A3B8' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      {
        id: 'e9-11',
        source: '9',
        target: '11',
        animated: true,
        style: { stroke: '#FB923C' },
        markerEnd: { type: MarkerType.ArrowClosed }
      }
    ];

    return { nodes, edges };
  };

  const { nodes: initialNodes, edges: initialEdges } = generateFlowchartElements();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update flowchart when dashboard data changes
  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = generateFlowchartElements();
    setNodes(newNodes);
    setEdges(newEdges);
  }, [dashboardData]);

  // Fetch state transitions data
  useEffect(() => {
    const fetchStateTransitions = async () => {
      try {
        setIsFlowLoading(true);
        const formattedFromDate = formatDateForAPI(fromDate, 'from');
        const formattedToDate = formatDateForAPI(toDate, 'to');
        
        const data = await getStateTransitions(
          formattedFromDate,
          formattedToDate,
          source,
          selectedRole,
          localSelectedRegion
        );
        setStateTransitions(data.state_transitions || []);
      } catch (error) {
        console.error('Error fetching state transitions:', error);
        setErrors(prev => ({
          ...prev,
          stateTransitions: "Failed to fetch state transitions data"
        }));
      } finally {
        setIsFlowLoading(false);
      }
    };

    fetchStateTransitions();
  }, [fromDate, toDate, source, selectedRole, localSelectedRegion, refreshTrigger]);

  // Generate nodes and edges for the lead flow
  const generateLeadFlowElements = () => {
    const statusMap = {
      'TOTAL_PASSED': { label: 'Total Passed', icon: FaUsers, className: 'bg-blue-50 border-blue-200', position: { x: 400, y: 0 } },
      'REGISTERED': { label: 'Registered', icon: FaUserCheck, className: 'bg-emerald-50 border-emerald-200', position: { x: 200, y: 100 } },
      'PENDING_REGISTRATION': { label: 'Pending Registration', icon: MdPendingActions, className: 'bg-amber-50 border-amber-200', position: { x: 600, y: 100 } },
      'TRAINING': { label: 'Training', icon: BsClockHistory, className: 'bg-cyan-50 border-cyan-200', position: { x: 400, y: 200 } },
      'ACTIVE': { label: 'Active', icon: FaUsers, className: 'bg-teal-50 border-teal-200', position: { x: 400, y: 300 } },
      'SUSPENDED': { label: 'Suspended', icon: FaUserSlash, className: 'bg-orange-50 border-orange-200', position: { x: 600, y: 300 } }
    };

    // Create nodes for all statuses that appear in transitions
    const usedStatuses = new Set();
    const nodes = [];
    const edges = [];

    // Process transitions to create nodes and track used statuses
    stateTransitions.forEach(transition => {
      if (transition.from_status !== 'FAILED') {
        usedStatuses.add(transition.from_status);
      }
      if (transition.to_status !== 'FAILED') {
        usedStatuses.add(transition.to_status);
      }
    });

    // Add Total Passed node
    nodes.push({
      id: 'TOTAL_PASSED',
      type: 'custom',
      position: statusMap['TOTAL_PASSED'].position,
      data: {
        label: statusMap['TOTAL_PASSED'].label,
        value: dashboardData.passed || 0,
        className: statusMap['TOTAL_PASSED'].className,
        icon: statusMap['TOTAL_PASSED'].icon
      }
    });

    // Calculate registered count from state transitions
    const registeredCount = stateTransitions
      .filter(t => t.from_status === 'CREATED' && t.to_status === 'REGISTERED')
      .reduce((sum, t) => sum + t.count, 0);

    // Add Registered node
    nodes.push({
      id: 'REGISTERED',
      type: 'custom',
      position: statusMap['REGISTERED'].position,
      data: {
        label: statusMap['REGISTERED'].label,
        value: registeredCount,
        className: statusMap['REGISTERED'].className,
        icon: statusMap['REGISTERED'].icon
      }
    });

    // Add Pending Registration node
    nodes.push({
      id: 'PENDING_REGISTRATION',
      type: 'custom',
      position: statusMap['PENDING_REGISTRATION'].position,
      data: {
        label: statusMap['PENDING_REGISTRATION'].label,
        value: dashboardData.pending_registration || 0,
        className: statusMap['PENDING_REGISTRATION'].className,
        icon: statusMap['PENDING_REGISTRATION'].icon
      }
    });

    // Create regular nodes for remaining statuses
    usedStatuses.forEach(status => {
      if (!['REGISTERED', 'PENDING_REGISTRATION'].includes(status) && statusMap[status]) {
        // Calculate total incoming transitions for this status
        const totalIncoming = stateTransitions
          .filter(t => t.to_status === status)
          .reduce((sum, t) => sum + t.count, 0);

        nodes.push({
          id: status,
          type: 'custom',
          position: statusMap[status].position,
          data: {
            label: statusMap[status].label,
            value: totalIncoming,
            className: statusMap[status].className,
            icon: statusMap[status].icon
          }
        });
      }
    });

    // Create two separate FAILED nodes
    const registeredFailedNode = {
      id: 'FAILED_FROM_REGISTERED',
      type: 'custom',
      position: { x: 200, y: 200 },
      data: {
        label: 'Failed',
        value: stateTransitions
          .filter(t => t.from_status === 'REGISTERED' && t.to_status === 'FAILED')
          .reduce((sum, t) => sum + t.count, 0),
        className: 'bg-rose-50 border-rose-200',
        icon: FaUserTimes
      }
    };

    const trainingFailedNode = {
      id: 'FAILED_FROM_TRAINING',
      type: 'custom',
      position: { x: 600, y: 200 },
      data: {
        label: 'Failed',
        value: stateTransitions
          .filter(t => t.from_status === 'TRAINING' && t.to_status === 'FAILED')
          .reduce((sum, t) => sum + t.count, 0),
        className: 'bg-rose-50 border-rose-200',
        icon: FaUserTimes
      }
    };

    nodes.push(registeredFailedNode, trainingFailedNode);

    // Create edges for the flow
    // Edge from Total Passed to Registered and Pending Registration
    edges.push(
      {
        id: 'e-total-registered',
        source: 'TOTAL_PASSED',
        target: 'REGISTERED',
        animated: true,
        style: { stroke: '#10B981' },
        markerEnd: { type: MarkerType.ArrowClosed }
      },
      {
        id: 'e-total-pending',
        source: 'TOTAL_PASSED',
        target: 'PENDING_REGISTRATION',
        animated: true,
        style: { stroke: '#F59E0B' },
        markerEnd: { type: MarkerType.ArrowClosed }
      }
    );

    // Add edges based on state transitions
    stateTransitions.forEach((transition, index) => {
      let edgeStyle = { stroke: '#60A5FA' }; // Default blue color
      
      if (transition.to_status === 'FAILED') {
        edgeStyle = { 
          stroke: '#EF4444',
          strokeWidth: 2,
          style: 'dashed'
        };

        // Create edges to the appropriate FAILED node
        edges.push({
          id: `e${index}`,
          source: transition.from_status,
          target: transition.from_status === 'REGISTERED' ? 'FAILED_FROM_REGISTERED' : 'FAILED_FROM_TRAINING',
          animated: true,
          style: edgeStyle,
          markerEnd: { type: MarkerType.ArrowClosed }
        });
      } else if (transition.to_status === 'SUSPENDED') {
        edgeStyle = { stroke: '#F97316' };
        edges.push({
          id: `e${index}`,
          source: transition.from_status,
          target: transition.to_status,
          animated: true,
          style: edgeStyle,
          markerEnd: { type: MarkerType.ArrowClosed }
        });
      } else if (transition.to_status === 'ACTIVE') {
        edgeStyle = { stroke: '#10B981' };
        edges.push({
          id: `e${index}`,
          source: transition.from_status,
          target: transition.to_status,
          animated: true,
          style: edgeStyle,
          markerEnd: { type: MarkerType.ArrowClosed }
        });
      } else if (transition.from_status !== 'FAILED' && transition.to_status !== 'FAILED' &&
                 !['TOTAL_PASSED', 'PENDING_REGISTRATION'].includes(transition.from_status)) {
        // Regular transitions (excluding FAILED state and special nodes)
        edges.push({
          id: `e${index}`,
          source: transition.from_status,
          target: transition.to_status,
          animated: true,
          style: edgeStyle,
          markerEnd: { type: MarkerType.ArrowClosed }
        });
      }
    });

    return { nodes, edges };
  };

  const { nodes: leadNodes, edges: leadEdges } = generateLeadFlowElements();

  if (Object.keys(errors).length > 0) {
    return (
      <div className="p-4">
        <DateFilter 
          onDateChange={handleDateChange} 
          onSourceChange={handleSourceChange}
          onRoleChange={handleRoleChange}
          onRegionChange={handleRegionChange}
        />
        {Object.entries(errors).map(([key, message]) => (
          <div key={key} className="mt-2 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{message}</p>
          </div>
        ))}
        {/* Rest of the dashboard content */}
        {/* ... existing JSX ... */}
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Global Date Filter - Stays at top */}
      <DateFilter 
        onDateChange={handleDateChange} 
        onSourceChange={handleSourceChange}
        onRoleChange={handleRoleChange}
        onRegionChange={handleRegionChange}
        showSourceFilter={activeTab === 'er' || activeTab === 'potential-leads'}  // Show source filter for both tabs
      />

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`py-2 px-4 mr-2 font-medium text-sm focus:outline-none ${
            activeTab === 'er'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => {
            setActiveTab('er');
            setSource(null);
            setSelectedRole(null);
            setSelectedRegion(null);
          }}
        >
          ER
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm focus:outline-none ${
            activeTab === 'potential-leads'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => {
            setActiveTab('potential-leads');
            setSelectedRole(null);
            setSelectedRegion(null);
          }}
        >
          Potential Leads
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'potential-leads' ? (
        <div>
          {/* Potential Leads Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {isMetricsLoading ? (
              <>
                <MetricShimmer />
                <MetricShimmer />
                <MetricShimmer />
                <MetricShimmer />
              </>
            ) : (
              <>
                {/* Total Leads */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Total Leads</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.total_leads || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <FaUsers className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                {/* Processed Leads */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Processed Leads</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.processed_leads || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <FaUserCheck className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Conversion Rate</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.total_leads ? 
                          `${((dashboardData.processed_leads / dashboardData.total_leads) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.total_leads ? 
                            Math.min(((dashboardData.processed_leads / dashboardData.total_leads) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Qualified Leads */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Qualified Leads</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.qualified_leads || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-yellow-100 rounded-full">
                      <BsFillPersonCheckFill className="w-6 h-6 text-yellow-600" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Success Rate</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.processed_leads ? 
                          `${((dashboardData.qualified_leads / dashboardData.processed_leads) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-yellow-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.processed_leads ? 
                            Math.min(((dashboardData.qualified_leads / dashboardData.processed_leads) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Rejected Leads */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Rejected Leads</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.rejected_leads || 0}
                      </p>
                    </div>
                    <div className={`p-3 ${COLORS.leads.rejected.bg} rounded-full`}>
                      <MdPendingActions className={`w-6 h-6 ${COLORS.leads.rejected.text}`} />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Rejection Rate</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.processed_leads ? 
                          `${((dashboardData.rejected_leads / dashboardData.processed_leads) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-rose-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.processed_leads ? 
                            Math.min(((dashboardData.rejected_leads / dashboardData.processed_leads) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Lead Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Lead Conversion Funnel */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Lead Conversion Funnel</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { 
                        name: 'Total Leads',
                        value: dashboardData.total_leads || 0,
                        fill: COLORS.leads.total.fill
                      },
                      { 
                        name: 'Processed',
                        value: dashboardData.processed_leads || 0,
                        fill: COLORS.leads.processed.fill
                      },
                      { 
                        name: 'Qualified',
                        value: dashboardData.qualified_leads || 0,
                        fill: COLORS.leads.qualified.fill
                      }
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#3B82F6">
                      {(data) => data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                      <LabelList
                        dataKey="value"
                        position="top"
                        content={({ value, x, y, width }) => {
                          if (typeof value !== 'number') return null;
                          
                          let percentage = 0;
                          if (value === dashboardData.total_leads) {
                            percentage = 100;
                          } else if (value === dashboardData.processed_leads) {
                            percentage = dashboardData.total_leads ? 
                              ((value / dashboardData.total_leads) * 100) : 0;
                          } else if (value === dashboardData.qualified_leads) {
                            percentage = dashboardData.processed_leads ? 
                              ((value / dashboardData.processed_leads) * 100) : 0;
                          }
                          
                          return (
                            <text 
                              x={x + width / 2} 
                              y={y - 10} 
                              textAnchor="middle"
                              fill="#666"
                              fontSize={12}
                            >
                              {`${value} (${percentage.toFixed(1)}%)`}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Leads by Source */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Leads by Source (Global)</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dashboardData.sourceData}
                      dataKey="count"
                      nameKey="source"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                    >
                      {dashboardData.sourceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS.sourceChart[index % COLORS.sourceChart.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* ER Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
            {isMetricsLoading ? (
              <>
                <MetricShimmer />
                <MetricShimmer />
                <MetricShimmer />
                <MetricShimmer />
              </>
            ) : (
              <>
                {/* Total ER */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Total ER</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.total_er || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-full">
                      <FaChartLine className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>

                {/* Total Passed */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Total Passed</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.passed || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-indigo-100 rounded-full">
                      <FaCalendarCheck className="w-6 h-6 text-indigo-600" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Of Total ER</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.total_er ? 
                          `${((dashboardData.passed / dashboardData.total_er) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.total_er ? 
                            Math.min(((dashboardData.passed / dashboardData.total_er) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Pending Registration */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Pending Registration</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.pending_registration || 0}
                      </p>
                    </div>
                    <div className={`p-3 ${COLORS.er.pending.bg} rounded-full`}>
                      <MdPendingActions className={`w-6 h-6 ${COLORS.er.pending.text}`} />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Of Total Passed</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.passed ? 
                          `${((dashboardData.pending_registration / dashboardData.passed) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-amber-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.passed ? 
                            Math.min(((dashboardData.pending_registration / dashboardData.passed) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Pending Training */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Pending Training</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.pending_training || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-full">
                      <BsClockHistory className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Of Total Passed</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.passed ? 
                          `${((dashboardData.pending_training / dashboardData.passed) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.passed ? 
                            Math.min(((dashboardData.pending_training / dashboardData.passed) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* In Training */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">In Training</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.in_training || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-teal-100 rounded-full">
                      <FaUserCheck className="w-6 h-6 text-teal-600" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Of Total Passed</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.passed ? 
                          `${((dashboardData.in_training / dashboardData.passed) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-teal-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.passed ? 
                            Math.min(((dashboardData.in_training / dashboardData.passed) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Active */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Active</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.active || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-emerald-100 rounded-full">
                      <FaUsers className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Of Total Passed</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.passed ? 
                          `${((dashboardData.active / dashboardData.passed) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.passed ? 
                            Math.min(((dashboardData.active / dashboardData.passed) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Failed */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Failed</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.failed || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-100 rounded-full">
                      <FaUserTimes className="w-6 h-6 text-gray-600" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Of Total Passed</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.passed ? 
                          `${((dashboardData.failed / dashboardData.passed) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gray-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.passed ? 
                            Math.min(((dashboardData.failed / dashboardData.passed) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Suspended */}
                <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Suspended</p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {dashboardData.suspended || 0}
                      </p>
                    </div>
                    <div className="p-3 bg-orange-100 rounded-full">
                      <FaUserSlash className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-gray-500">Of Total Passed</span>
                      <span className="text-sm font-medium text-gray-900">
                        {dashboardData.passed ? 
                          `${((dashboardData.suspended / dashboardData.passed) * 100).toFixed(1)}%` 
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-orange-600 h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${dashboardData.passed ? 
                            Math.min(((dashboardData.suspended / dashboardData.passed) * 100), 100) : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Lead Flow Visualization */}
          {/* <div className="mb-8 bg-white rounded-lg shadow-md border border-gray-200">
            <button
              onClick={() => setShowLeadFlow(!showLeadFlow)}
              className="w-full px-6 py-4 flex items-center justify-between text-lg font-semibold text-gray-700 hover:bg-gray-50"
            >
              <span className="flex items-center">
                <FaChartLine className="mr-2 text-blue-600" />
                Lead Flow Visualization
              </span>
              {showLeadFlow ? <FaChevronUp /> : <FaChevronDown />}
            </button>
            
            {showLeadFlow && (
              <div className="p-6 border-t border-gray-200">
                {isFlowLoading ? (
                  <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
                  </div>
                ) : (
                  <>
                    <div style={{ height: '600px' }} className="bg-gray-50 rounded-lg">
                      <ReactFlow
                        nodes={leadNodes}
                        edges={leadEdges}
                        nodeTypes={nodeTypes}
                        fitView
                        connectionMode={ConnectionMode.STRICT}
                        minZoom={0.5}
                        maxZoom={2}
                        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                        className="rounded-lg"
                      />
                    </div>
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Total Passed</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Registered</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-cyan-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">In Training</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Active</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-rose-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Failed</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                        <span className="text-sm text-gray-600">Suspended</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div> */}

          {/* Agent Statistics Section */}
          <div className="mb-8 bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4">
              <h3 className="text-lg font-semibold mb-4 lg:mb-0">Agent Statistics</h3>
              <div className="w-full lg:w-auto flex flex-col md:flex-row gap-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search agents..."
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Registration</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Training</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Training</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rejected</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isStatsLoading ? (
                    <>
                      {[...Array(5)].map((_, index) => (
                        <TableRowShimmer key={index} />
                      ))}
                    </>
                  ) : getCurrentPageItems().length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                        No data available
                      </td>
                    </tr>
                  ) : (
                    getCurrentPageItems().map((stat, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {stat.created_by}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {stat.total}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {stat.pending_registration}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {stat.pending_training}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {stat.training}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {stat.active}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {stat.rejected}
                        </td>
                        
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Load More Button */}
            {hasMore && !isStatsLoading && (
              <div className="mt-4 text-center">
                <button
                  onClick={loadMoreData}
                  disabled={isLoadingMore}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isLoadingMore ? 'Loading...' : 'Load More Data'}
                </button>
              </div>
            )}

            {/* Pagination */}
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between mt-4">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {agentStats.items.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, agentStats.items.length)}
                  </span>{' '}
                  of <span className="font-medium">{agentStats.items.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  {renderPaginationButtons()}
                </nav>
              </div>
            </div>
          </div>

          {/* ER Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* ER Status Distribution */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">ER Status Distribution</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { name: 'Pending Registration', value: dashboardData.pending_registration || 0, fill: COLORS.er.pending.fill },
                      { name: 'Pending Training', value: dashboardData.pending_training || 0, fill: COLORS.er.training.fill },
                      { name: 'In Training', value: dashboardData.in_training || 0, fill: COLORS.er.active.fill },
                      { name: 'Active', value: dashboardData.active || 0, fill: COLORS.leads.qualified.fill },
                      { name: 'Failed', value: dashboardData.failed || 0, fill: COLORS.er.failed.fill },
                      { name: 'Suspended', value: dashboardData.suspended || 0, fill: COLORS.er.suspended.fill }
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value">
                      {(data) => data.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.fill}
                        />
                      ))}
                      <LabelList
                        dataKey="value"
                        position="top"
                        content={({ value, x, y, width }) => {
                          if (!value) return null;
                          return (
                            <text 
                              x={x + width / 2} 
                              y={y - 10} 
                              textAnchor="middle"
                              fill="#059669"
                              fontSize={12}
                              fontWeight="500"
                            >
                              {value}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Regional Performance */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Regional Performance (Global)</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dashboardData.regions}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                      interval={0}
                    />
                    <YAxis />
                    <Tooltip 
                      cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                    />
                    <Bar 
                      dataKey="earlyRegistrations"
                      fill="#34D399"
                    >
                      {dashboardData.regions.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={COLORS.regionalChart[index % COLORS.regionalChart.length]}
                        />
                      ))}
                      <LabelList
                        dataKey="earlyRegistrations"
                        position="top"
                        content={({ value, x, y, width }) => {
                          if (!value) return null;
                          return (
                            <text 
                              x={x + width / 2} 
                              y={y - 10} 
                              textAnchor="middle"
                              fill="#059669"
                              fontSize={12}
                              fontWeight="500"
                            >
                              {value}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/agent-management"
              className="flex items-center p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-all"
            >
              <FaUsers className="w-5 h-5 text-indigo-600 mr-3" />
              <span className="text-indigo-600 font-medium">Manage Agents</span>
            </Link>
            <Link
              to="/potential-leads"
              className="flex items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-all"
            >
              <FaChartLine className="w-5 h-5 text-green-600 mr-3" />
              <span className="text-green-600 font-medium">View Leads</span>
            </Link>
          </div>

          {/* Recent Activities */}
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Recent Registrations</h3>
            <div className="space-y-4">
              {isMetricsLoading ? (
                <>
                  {[...Array(5)].map((_, index) => (
                    <ActivityShimmer key={index} />
                  ))}
                </>
              ) : (
                dashboardData.recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      <MdOutlinePersonPin className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                      <p className="text-sm text-gray-500">{activity.time}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
